// lib/auth.ts
// Access token — только в памяти (защита от XSS)
// Refresh token — httpOnly cookie (бэк управляет сам)
import { debugLog } from "@/lib/debug";

const KEYS = {
  accessToken: "salaria_at",
  //refreshToken: "salaria_rt",   // ← эта строка должна быть!
  rememberMe: "salaria_rm",
} as const;

// ── Тип пользователя ──────────────────────────────────────
export interface SalariaUser {
  id:    string;
  email: string;
  name:  string;
  role:  string;
  tier:  "F1_0" | "M2_0" | "L3_0" | "XL4_0";
  energyBalance: number;
  balanceUsd: number;
}

export type Tier = SalariaUser["tier"];

// ── Access token (in-memory only) ─────────────────────────
let inMemoryToken: string | null = null;
if (typeof window !== 'undefined') {
  (window as any).__getToken = () => inMemoryToken;
}

export function setAccessToken(token: string) {
  inMemoryToken = token;
}

export function getAccessToken(): string | null {
  return inMemoryToken;
}

export function clearAccessToken() {
  inMemoryToken = null;
}

//export function getRefreshToken(): string | null {
 // if (typeof window === "undefined") return null;
//  return (
 //   localStorage.getItem(KEYS.refreshToken) ??
 //   sessionStorage.getItem(KEYS.refreshToken)
 // );
//}

// ── RememberMe (единственное что в localStorage) ─────────
export function saveRememberMe(value: boolean) {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEYS.rememberMe, String(value));
}

export function getRememberMe(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(KEYS.rememberMe) === "true";
}

// ── Logout / очистка ─────────────────────────────────────
export function clearTokens() {
  inMemoryToken = null;
  lastRefreshFailAt = Date.now(); // взводим cooldown — рвём цикл
  if (typeof window !== "undefined") {
    localStorage.removeItem(KEYS.rememberMe);
    // refreshToken — HttpOnly, JS его удалить НЕ может.
    // Чистим только non-HttpOnly cookies на фронт-домене (если завелись).
    document.cookie = "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
  }
}

// ── Пользователь из JWT ──────────────────────────────────
export function getUserFromToken(): SalariaUser | null {
  if (!inMemoryToken) return null;

  try {
    const payload = JSON.parse(atob(inMemoryToken.split(".")[1]));

    if (!payload?.sub) return null;

   return {
      id:    payload.sub,
      email: payload.email ?? "",
      name:  payload.email?.split("@")[0] ?? "Вы",
      role:  payload.role ?? "USER",
      tier:  payload.tier ?? "F1_0",
      energyBalance: payload.energyBalance ?? 0,
      balanceUsd: payload.balanceUsd ?? 0,
    };
  } catch {
    return null;
  }
}

// ── Auth helpers ─────────────────────────────────────────
export function isLoggedIn(): boolean {
  return !!inMemoryToken;
}

// Проверка истёк ли JWT (с 5-секундным skew). Без токена → считаем истёкшим.
export function isAccessTokenExpired(token: string | null = inMemoryToken): boolean {
  if (!token) return true;
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    if (!exp) return true;
    return exp * 1000 < Date.now() - 5000;
  } catch {
    return true;
  }
}

// ── Tier helpers ─────────────────────────────────────────
const TIER_ORDER: Tier[] = ["F1_0", "M2_0", "L3_0", "XL4_0"];

export function hasTierAccess(userTier: Tier, requiredTier: Tier): boolean {
  return TIER_ORDER.indexOf(userTier) >= TIER_ORDER.indexOf(requiredTier);
}

export function getTierLabel(tier: string): string {
  const map: Record<string, string> = {
    F1_0: "F1.0",
    M2_0: "M2.0", 
    L3_0: "L3.0",
    XL4_0: "XL4.0",
  };
  return map[tier] ?? tier;
}

export function getTierColor(tier: string): string {
  const map: Record<string, string> = {
    FREE: "bg-zinc-100 text-zinc-600",   F1_0: "bg-zinc-100 text-zinc-600",
    PREMIUM: "bg-blue-50 text-blue-700", M2_0: "bg-blue-50 text-blue-700",
    PRO: "bg-purple-50 text-purple-700", L3_0: "bg-purple-50 text-purple-700",
    ENTERPRISE: "bg-amber-50 text-amber-700", XL4_0: "bg-amber-50 text-amber-700",
  };
  return map[tier] ?? "bg-zinc-100 text-zinc-600";
}

let refreshInFlight: Promise<any> | null = null;
let lastRefreshFailAt = 0;
const REFRESH_FAIL_COOLDOWN_MS = 15_000; // 15 сек тишины после фейла — рвёт loop

// Сбросить cooldown (после удачного signin/login)
export function resetRefreshCooldown() {
  lastRefreshFailAt = 0;
}

export const refreshAccessToken = async () => {
  // 1. Cooldown: если только что упало — не звоним снова, рвём цикл.
  const sinceFail = Date.now() - lastRefreshFailAt;
  if (sinceFail < REFRESH_FAIL_COOLDOWN_MS) {
    debugLog('AUTH', 'refresh cooldown active, skip', { sinceFailMs: sinceFail });
    return null;
  }

  // 2. Single-flight: все параллельные ждут один и тот же promise.
  if (refreshInFlight) {
    debugLog('AUTH', 'refresh already in flight, awaiting', {});
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    debugLog('AUTH', 'refreshAccessToken called', {});
    try {
      const res = await fetch(`/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      debugLog('AUTH', 'refresh response', { status: res.status });

      if (!res.ok) {
        // 401/403 — refresh окончательно мёртв. Чистим in-memory и взводим cooldown.
        // 5xx/network — тоже cooldown, чтобы не долбить сервер.
        lastRefreshFailAt = Date.now();
        if (res.status === 401 || res.status === 403) {
          inMemoryToken = null;
        }
        debugLog('AUTH', 'refresh failed, cooldown armed', { status: res.status });
        return null;
      }

      const data = await res.json();
      return data;
    } catch (err) {
      lastRefreshFailAt = Date.now();
      const msg = err instanceof Error ? err.message : String(err);
      debugLog('AUTH', 'refresh network error', { error: msg });
      return null;
    } finally {
      // отпускаем gate чуть позже — догнать burst параллельных fetch'ей
      setTimeout(() => { refreshInFlight = null; }, 200);
    }
  })();

  return refreshInFlight;
}